import { useMutation, useQuery } from '@tanstack/react-query'
import { clearLocalStorage } from 'api/api.utils'
import { apiClient } from 'api/apiClient/apiClient'
import { useTranslation } from 'react-i18next'
import alertStore from 'store/alert/alert'
import { useAuth } from 'ui/components/AuthContext/AuthContext'

export type LogInResponse = {
  key: string
  user: User
}

type AccountActivationUrlValidityResponse = {
  is_valid: boolean
}

type AccountActivationData = { email: string; token: string; passwd: string; confirm_passwd: string }

export function useAccountActivationUrlValidity() {
  return useMutation({
    mutationFn: ({ email, token }: { email: string; token: string }) =>
      apiClient<AccountActivationUrlValidityResponse>(`users/activation_url_is_valid`, {
        params: { email: email, token: token },
      }),
  })
}

export function useAccountActivation() {
  return useMutation({
    mutationFn: (data: AccountActivationData) => apiClient(`users/activate_account`, { data }),
  })
}

export type ProviderResponse = {
  email_password: boolean
  sso_providers: SSOProvider[]
}
export type SSOProvider = {
  provider_id: string
  client_id: string
  server_url: string
  button_name: string
}

export function useProviders(email: string) {
  return useQuery({
    queryKey: ['auth', 'providers', email],
    queryFn: async () => {
      return apiClient<ProviderResponse>(`auth/providers/${email}`)
    },
    enabled: email.length > 0,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

export class SSOEmailMismatchError extends Error {
  constructor(
    public expectedEmail: string,
    public authenticatedEmail: string
  ) {
    super(`SSO email mismatch: expected ${expectedEmail}, got ${authenticatedEmail}`)
    this.name = 'SSOEmailMismatchError'
  }
}

export function useSSOLoginMutation() {
  const { t } = useTranslation()
  const { setUser } = useAuth()
  return useMutation({
    mutationFn: async ({
      code,
      state,
      provider,
      callback_url,
      expected_email,
    }: {
      code: string
      state: string
      provider: string
      callback_url: string
      expected_email: string
    }) => {
      const response = await apiClient<LogInResponse>(`auth/oidc`, {
        data: {
          code: code,
          state: state,
          provider_id: provider,
          callback_url: callback_url,
          scope: 'User.Read',
          expected_email: expected_email,
        },
      })

      if (expected_email && response?.user?.email) {
        const authenticatedEmail = response.user.email.toLowerCase()
        const expectedEmail = expected_email.toLowerCase()

        if (authenticatedEmail !== expectedEmail) {
          throw new SSOEmailMismatchError(expectedEmail, authenticatedEmail)
        }
      }

      return response
    },

    onError: (error) => {
      if (error instanceof SSOEmailMismatchError) {
        alertStore.error(
          t('The SSO account you authenticated with does not match the entered email. Please try again.')
        )
      } else {
        alertStore.error(t(`Could not login. Please check your credentials and try again`))
      }
      localStorage.removeItem(`sso_auth_state`)
      window.history.replaceState({}, document.title, window.location.pathname)
    },
    onSuccess: (response) => {
      if (response?.key) {
        const token = response.key
        const user: User = { ...response.user, token }
        setUser(user)
      } else {
        localStorage.removeItem(`sso_auth_state`)
        window.history.replaceState({}, document.title, window.location.pathname)
        throw new Error('Authentication failed: No token received')
      }
    },
  })
}

export function useLoginMutation() {
  const { t } = useTranslation()
  const { setUser } = useAuth()
  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      apiClient<LogInResponse>(`auth/login`, {
        data: {
          email: email.toLowerCase(),
          password: password,
        },
      }),

    onError: () => {
      alertStore.error(t(`Could not login. Please check your credentials and try again`))
    },

    onSuccess: (response) => {
      if (!response) {
        throw new Error('Login failed: No response received')
      }

      if (response.key) {
        // User successfully logged in
        const token = response.key
        const user: User = { ...response.user, token }
        setUser(user)
      }
    },
  })
}

export function useLogoutMutation() {
  return useMutation({
    mutationFn: () => apiClient(`auth/logout`, { method: `POST` }),

    onSuccess: () => {
      clearLocalStorage()
      document.title = `Aurora by Sigholm`
      window.location.replace(`/`)
    },
  })
}
export function useChangePasswordMutation() {
  const { t } = useTranslation()

  return useMutation({
    mutationFn: ({
      newPassword1,
      newPassword2,
      currentPassword,
    }: {
      newPassword1: string
      newPassword2: string
      currentPassword: string
    }) =>
      apiClient(`auth/password/change`, {
        data: {
          new_password1: newPassword1,
          new_password2: newPassword2,
          old_password: currentPassword,
        },
      }),

    onError: () => {
      alertStore.error(t(`We could not update your password due to current password do not match, please try again`))
    },

    onSuccess: () => {
      alertStore.success(t(`The password has been updated`))
    },
  })
}

export type ResetPasswordData = {
  email: string
  captchaValue: string
}

export function useResetPasswordMutation() {
  return useMutation({
    mutationFn: ({ email, captchaValue }: ResetPasswordData) =>
      apiClient(`auth/password/reset`, {
        data: {
          email: email.toLowerCase(),
          captcha_value: captchaValue,
        },
      }),
  })
}
export function useConfirmResetPasswordMutation() {
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({
      newPassword1,
      newPassword2,
      uid,
      token,
    }: {
      newPassword1: string
      newPassword2: string
      uid: string
      token: string
    }) =>
      apiClient(`auth/password/reset/confirm`, {
        data: {
          new_password1: newPassword1,
          new_password2: newPassword2,
          uid: uid,
          token: token,
        },
      }),

    onError: () => {
      alertStore.error(
        t(
          `Your password could not be changed. Please check that the password meet all the criteras and that your reset link is still valid.`
        )
      )
    },

    onSuccess: () => {
      alertStore.success(t(`Your password is now reset. You may now log in with your new password!`))
      window.location.replace(`/`)
    },
  })
}

export function useImpersonateMutation() {
  const { t } = useTranslation()
  const { user, setUser, setImpersonatingUser } = useAuth()
  return useMutation({
    mutationFn: ({ userId }: { userId: number }) =>
      apiClient<LogInResponse>(`auth/impersonate_user`, {
        data: {
          user_id: userId,
        },
      }),

    onError: () => {
      alertStore.error(t(`Could not start impersonation. Please try again.`))
    },

    onSuccess: (response) => {
      if (!response) {
        throw new Error('Impersonation failed: No response received')
      }

      if (response?.key) {
        const token = response.key
        const impersonatedUser: User = { ...response.user, token }
        setImpersonatingUser(user)
        setUser(impersonatedUser)
      }
    },
  })
}
